import { Component, input, output, forwardRef, signal, ElementRef, ViewChild, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';
import { ComponentSize } from '../types';

export interface MultiSelectOption {
  label: string;
  value: any;
  disabled?: boolean;
  icon?: string;
  group?: string;
}

@Component({
  selector: 'app-multi-select',
  imports: [CommonModule, FormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiSelectComponent),
      multi: true
    }
  ],
  template: `
    <div class="relative" #container>
      @if (label()) {
        <label 
          [for]="id() || name()"
          class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
          {{ label() }}
          @if (required()) {
            <span class="text-red-500 ml-1">*</span>
          }
        </label>
      }
      
      <div class="relative">
        <button
          type="button"
          [id]="id() || name()"
          [disabled]="disabled()"
          [class]="buttonClasses"
          (click)="toggleDropdown()"
          [attr.aria-expanded]="isOpen()"
          [attr.aria-haspopup]="'listbox'">
          <div class="flex items-center gap-2 flex-1 text-left flex-wrap">
            @if (selectedOptions().length === 0) {
              <span class="text-gray-400 dark:text-gray-500">{{ placeholder() }}</span>
            } @else {
              @for (option of selectedOptions(); track option.value) {
                <span [class]="chipClasses">
                  {{ option.label }}
                  <button
                    type="button"
                    (click)="removeOption(option.value); $event.stopPropagation()"
                    class="ml-1 hover:text-gray-700 dark:hover:text-gray-300">
                    <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </span>
              }
              @if (selectedOptions().length > maxVisible()) {
                <span class="text-sm text-gray-600 dark:text-gray-400">
                  +{{ selectedOptions().length - maxVisible() }} more
                </span>
              }
            }
          </div>
          <svg 
            class="w-4 h-4 transition-transform duration-200 flex-shrink-0"
            [class.rotate-180]="isOpen()"
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        
        @if (isOpen()) {
          <div
            #dropdownMenu
            [class]="menuClasses"
            role="listbox">
            @if (filterable()) {
              <div class="p-2 border-b border-gray-200 dark:border-gray-700">
                <input
                  type="text"
                  [(ngModel)]="filterValue"
                  (ngModelChange)="onFilterChange()"
                  placeholder="Search..."
                  class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100" />
              </div>
            }
            
            @if (selectAll()) {
              <button
                type="button"
                (click)="toggleSelectAll()"
                [class]="selectAllClasses">
                <input
                  type="checkbox"
                  [checked]="isAllSelected()"
                  class="w-4 h-4 text-blue-600 rounded focus:ring-blue-500" />
                <span>Select All</span>
              </button>
            }
            
            <div class="max-h-60 overflow-y-auto">
              @for (group of groupedOptions; track group.name || $index) {
                @if (group.name) {
                  <div class="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                    {{ group.name }}
                  </div>
                }
                @for (option of group.options; track option.value) {
                  <button
                    type="button"
                    role="option"
                    [disabled]="option.disabled"
                    [class]="getOptionClasses(option)"
                    (click)="toggleOption(option)"
                    [attr.aria-selected]="isSelected(option)">
                    <input
                      type="checkbox"
                      [checked]="isSelected(option)"
                      [disabled]="option.disabled"
                      class="w-4 h-4 text-blue-600 rounded focus:ring-blue-500" />
                    @if (option.icon) {
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" [attr.d]="option.icon" />
                      </svg>
                    }
                    <span class="flex-1 text-left">{{ option.label }}</span>
                  </button>
                }
              }
            </div>
          </div>
        }
      </div>
      
      @if (helpText() && !hasError) {
        <span class="text-xs text-gray-500 dark:text-gray-400 mt-1 block">
          {{ helpText() }}
        </span>
      }
      
      @if (hasError) {
        <span class="text-xs text-red-500 dark:text-red-400 mt-1 block font-medium">
          {{ displayError }}
        </span>
      }
    </div>
  `,
  styles: []
})
export class MultiSelectComponent implements ControlValueAccessor {
  label = input<string>('');
  placeholder = input<string>('Select options');
  options = input<MultiSelectOption[]>([]);
  required = input<boolean>(false);
  disabled = input<boolean>(false);
  filterable = input<boolean>(false);
  selectAll = input<boolean>(true);
  maxVisible = input<number>(3);
  errorMessage = input<string>('');
  helpText = input<string>('');
  id = input<string>('');
  name = input<string>('');
  class = input<string>('');
  size = input<ComponentSize>('md');

  changeEvent = output<any[]>();

  @ViewChild('container', { static: false }) container!: ElementRef<HTMLElement>;
  @ViewChild('dropdownMenu', { static: false }) dropdownMenu!: ElementRef<HTMLElement>;

  value = signal<any[]>([]);
  isOpen = signal(false);
  filterValue = '';
  touched = signal(false);

  private onChange = (value: any[]) => {};
  private onTouched = () => {};

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent): void {
    if (!this.isOpen()) return;
    const target = event.target as HTMLElement;
    if (this.container?.nativeElement?.contains(target)) return;
    this.isOpen.set(false);
  }

  toggleDropdown(): void {
    if (!this.disabled()) {
      this.isOpen.update(v => !v);
    }
  }

  toggleOption(option: MultiSelectOption): void {
    if (option.disabled) return;
    const current = [...this.value()];
    const index = current.indexOf(option.value);
    
    if (index > -1) {
      current.splice(index, 1);
    } else {
      current.push(option.value);
    }
    
    this.value.set(current);
    this.onChange(current);
    this.changeEvent.emit(current);
    this.markAsTouched();
  }

  removeOption(optionValue: any): void {
    const current = [...this.value()];
    const index = current.indexOf(optionValue);
    if (index > -1) {
      current.splice(index, 1);
      this.value.set(current);
      this.onChange(current);
      this.changeEvent.emit(current);
    }
  }

  toggleSelectAll(): void {
    const filtered = this.filteredOptions;
    const allSelected = filtered.every(opt => this.isSelected(opt) || opt.disabled);
    
    if (allSelected) {
      // Deselect all filtered
      const current = this.value().filter(val => 
        !filtered.some(opt => opt.value === val)
      );
      this.value.set(current);
    } else {
      // Select all filtered
      const current = [...this.value()];
      filtered.forEach(opt => {
        if (!opt.disabled && !current.includes(opt.value)) {
          current.push(opt.value);
        }
      });
      this.value.set(current);
    }
    
    this.onChange(this.value());
    this.changeEvent.emit(this.value());
  }

  isSelected(option: MultiSelectOption): boolean {
    return this.value().includes(option.value);
  }

  isAllSelected(): boolean {
    const filtered = this.filteredOptions;
    return filtered.length > 0 && filtered.every(opt => this.isSelected(opt) || opt.disabled);
  }

  selectedOptions(): MultiSelectOption[] {
    return this.options().filter(opt => this.value().includes(opt.value));
  }

  onFilterChange(): void {
    // Filtering handled by filteredOptions getter
  }

  get filteredOptions(): MultiSelectOption[] {
    if (!this.filterValue) return this.options();
    const filter = this.filterValue.toLowerCase();
    return this.options().filter(opt => 
      opt.label.toLowerCase().includes(filter) ||
      (opt.group && opt.group.toLowerCase().includes(filter))
    );
  }

  get groupedOptions(): { name?: string; options: MultiSelectOption[] }[] {
    const filtered = this.filteredOptions;
    const groups = new Map<string, MultiSelectOption[]>();
    const ungrouped: MultiSelectOption[] = [];

    filtered.forEach(option => {
      if (option.group) {
        if (!groups.has(option.group)) {
          groups.set(option.group, []);
        }
        groups.get(option.group)!.push(option);
      } else {
        ungrouped.push(option);
      }
    });

    const result: { name?: string; options: MultiSelectOption[] }[] = [];
    groups.forEach((options, name) => {
      result.push({ name, options });
    });
    if (ungrouped.length > 0) {
      result.push({ options: ungrouped });
    }

    return result;
  }

  getOptionClasses(option: MultiSelectOption): string {
    const classes = [
      'w-full',
      'px-4',
      'py-2',
      'text-left',
      'flex',
      'items-center',
      'gap-2',
      'transition-colors',
      'duration-150',
      'focus:outline-none'
    ];

    if (option.disabled) {
      classes.push('opacity-50', 'cursor-not-allowed');
    } else {
      classes.push('hover:bg-gray-100', 'dark:hover:bg-gray-700', 'cursor-pointer');
    }

    if (this.isSelected(option)) {
      classes.push('bg-blue-50', 'dark:bg-blue-900/20');
    } else {
      classes.push('text-gray-700', 'dark:text-gray-300');
    }

    return classes.join(' ');
  }

  get selectAllClasses(): string {
    return 'w-full px-4 py-2 text-left flex items-center gap-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer border-b border-gray-200 dark:border-gray-700';
  }

  get chipClasses(): string {
    return 'inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-md';
  }

  get buttonClasses(): string {
    const classes = [
      'w-full',
      'px-4',
      'py-3',
      'border',
      'rounded-lg',
      'flex',
      'items-center',
      'justify-between',
      'gap-2',
      'transition-all',
      'duration-200',
      'focus:outline-none',
      'text-left',
      'min-h-[42px]'
    ];

    const sizeMap = {
      xs: 'px-2 py-1 text-xs min-h-[32px]',
      sm: 'px-3 py-2 text-sm min-h-[36px]',
      md: 'px-4 py-3 text-base min-h-[42px]',
      lg: 'px-5 py-4 text-lg min-h-[48px]',
      xl: 'px-6 py-5 text-xl min-h-[54px]'
    };
    classes.push(sizeMap[this.size()]);

    if (this.hasError) {
      classes.push('border-red-500', 'focus:border-red-500', 'focus:ring-2', 'focus:ring-red-100', 'dark:focus:ring-red-900/30');
    } else {
      classes.push('border-gray-300', 'dark:border-gray-600', 'focus:border-blue-600', 'dark:focus:border-blue-500', 'focus:ring-2', 'focus:ring-blue-100', 'dark:focus:ring-blue-900/30');
    }

    if (this.disabled()) {
      classes.push('opacity-60', 'cursor-not-allowed', 'bg-gray-50', 'dark:bg-gray-800');
    } else {
      classes.push('bg-white', 'dark:bg-gray-800', 'text-gray-900', 'dark:text-gray-100', 'hover:border-gray-400', 'dark:hover:border-gray-500');
    }

    const customClass = this.class();
    if (customClass) {
      classes.push(customClass);
    }

    return classes.join(' ');
  }

  get menuClasses(): string {
    return 'absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-60 overflow-hidden';
  }

  get hasError(): boolean {
    return this.touched() && !!this.errorMessage();
  }

  get displayError(): string {
    return this.errorMessage() || '';
  }

  private markAsTouched(): void {
    if (!this.touched()) {
      this.touched.set(true);
    }
  }

  writeValue(value: any[]): void {
    this.value.set(Array.isArray(value) ? value : []);
  }

  registerOnChange(fn: (value: any[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    // Handled by input signal
  }
}

